/**
 * Konobox per-song pricing rules (KAN-697, story chain KAN-697 -> KAN-698 -> KAN-699).
 *
 * One price per song plus four song-count packages per store. Package price = songs x price per song.
 * The gateway (KAN-698) and the CoinSing kiosk (KAN-699) implement the same rules; all three replay
 * `shared/konobox-pricing-golden-vectors.json`.
 *
 * Money is handled in integer cents. A value is a valid price when it is a finite number > 0 whose
 * hundredths are whole (abs(v * 100 - round(v * 100)) < 1e-6).
 */

export const KONOBOX_PRICING_DEFAULTS = {
  pricePerSong: 2.0,
  songCounts: [2, 4, 6, 8]
} as const;

export const KONOBOX_PRICING_LIMITS = {
  minSongs: 1,
  maxSongs: 20,
  maxDecimals: 2
} as const;

export const KONOBOX_PACKAGE_SLOTS = 4;

/** Marker the gateway sends for a stored value it could not use. */
export const KONOBOX_INVALID_MARKER = 'invalid';

const CENTS_TOLERANCE = 1e-6;

export interface KonoboxDisc {
  songCount: number | null;
  price: number | null;
  enabled: boolean;
}

export interface KonoboxPricingResolution {
  pricePerSong: number | null;
  discs: KonoboxDisc[];
  isDefault: boolean;
  isComplete: boolean;
}

export interface KonoboxPricingRaw {
  pricePerSong?: unknown;
  songCounts?: unknown;
}

/** Whole cents for a price, or null when the value is not a finite number with whole hundredths. */
export function toCents(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const hundredths = value * 100;
  const rounded = Math.round(hundredths);
  if (Math.abs(hundredths - rounded) >= CENTS_TOLERANCE) return null;
  return rounded;
}

/** Package price in dollars: songCount x pricePerSong, computed in integer cents. */
export function packagePrice(songCount: number, pricePerSong: number): number {
  const cents = toCents(pricePerSong);
  if (cents === null) return NaN;
  return (songCount * cents) / 100;
}

function isAbsent(value: unknown): boolean {
  return value === undefined || value === null;
}

function isValidPrice(value: unknown): value is number {
  const cents = toCents(value);
  return cents !== null && cents > 0;
}

function isValidCount(value: unknown): value is number {
  return typeof value === 'number'
    && Number.isInteger(value)
    && value >= KONOBOX_PRICING_LIMITS.minSongs
    && value <= KONOBOX_PRICING_LIMITS.maxSongs;
}

const DISABLED_DISC: KonoboxDisc = { songCount: null, price: null, enabled: false };

/**
 * Resolves raw stored values (as read from Firestore) into the price per song and the four packages.
 * An absent (undefined or null) field takes its default independently; a present field is validated and
 * never repaired. Valid packages are sorted ascending, disabled ones come last.
 */
export function resolveKonoboxPricing(raw: KonoboxPricingRaw | null | undefined): KonoboxPricingResolution {
  const source = raw ?? {};
  const priceAbsent = isAbsent(source.pricePerSong);
  const countsAbsent = isAbsent(source.songCounts);

  const rawPrice = priceAbsent ? KONOBOX_PRICING_DEFAULTS.pricePerSong : source.pricePerSong;
  const rawCounts: unknown = countsAbsent ? KONOBOX_PRICING_DEFAULTS.songCounts : source.songCounts;

  const priceValid = isValidPrice(rawPrice);
  const pricePerSong = priceValid ? (rawPrice as number) : null;

  let discs: KonoboxDisc[];
  if (!priceValid || !Array.isArray(rawCounts) || rawCounts.length !== KONOBOX_PACKAGE_SLOTS) {
    discs = Array.from({ length: KONOBOX_PACKAGE_SLOTS }, () => ({ ...DISABLED_DISC }));
  } else {
    const occurrences = new Map<unknown, number>();
    rawCounts.forEach(c => occurrences.set(c, (occurrences.get(c) ?? 0) + 1));
    const entries: KonoboxDisc[] = rawCounts.map(c => {
      if (!isValidCount(c) || (occurrences.get(c) ?? 0) > 1) return { ...DISABLED_DISC };
      return { songCount: c, price: packagePrice(c, pricePerSong as number), enabled: true };
    });
    const enabled = entries.filter(d => d.enabled).sort((a, b) => (a.songCount as number) - (b.songCount as number));
    const disabled = entries.filter(d => !d.enabled);
    discs = [...enabled, ...disabled];
  }

  return {
    pricePerSong,
    discs,
    isDefault: priceAbsent || countsAbsent,
    isComplete: discs.every(d => d.enabled)
  };
}

// ---------------------------------------------------------------------------
// Store Management form
// ---------------------------------------------------------------------------

export interface KonoboxPricingFormValues {
  pricePerSong: number | null;
  songCounts: (number | null)[];
  /** The price per song was absent on the store and the default was filled in. */
  priceDefaulted: boolean;
  /** The song packages were absent on the store and the defaults were filled in. */
  countsDefaulted: boolean;
}

/**
 * Values the form shows for a stored store (or an untouched new store): an absent field gets its default
 * independently, a present field is shown as stored even when invalid (the errors are then visible on open).
 */
export function konoboxPricingFormFromStore(raw: KonoboxPricingRaw | null | undefined): KonoboxPricingFormValues {
  const source = raw ?? {};
  const priceDefaulted = isAbsent(source.pricePerSong);
  const countsDefaulted = isAbsent(source.songCounts);

  const pricePerSong = priceDefaulted
    ? KONOBOX_PRICING_DEFAULTS.pricePerSong
    : (source.pricePerSong as number | null);

  let songCounts: (number | null)[];
  if (countsDefaulted) {
    songCounts = [...KONOBOX_PRICING_DEFAULTS.songCounts];
  } else if (Array.isArray(source.songCounts) && source.songCounts.length <= KONOBOX_PACKAGE_SLOTS) {
    songCounts = Array.from({ length: KONOBOX_PACKAGE_SLOTS }, (_, i) => {
      const c = (source.songCounts as unknown[])[i];
      return isAbsent(c) ? null : (c as number);
    });
  } else {
    songCounts = Array.from({ length: KONOBOX_PACKAGE_SLOTS }, () => null);
  }

  return { pricePerSong, songCounts, priceDefaulted, countsDefaulted };
}

export interface KonoboxPricingFormErrors {
  price: string | null;
  counts: (string | null)[];
  hasErrors: boolean;
}

export const KONOBOX_PRICING_SUMMARY_ERROR = 'Fix the highlighted Konobox pricing fields before saving.';

function isEmptyInput(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

/** Inline validation of the price per song and the four package inputs. */
export function validateKonoboxPricingForm(form: {
  pricePerSong: unknown;
  songCounts: unknown[];
}): KonoboxPricingFormErrors {
  let price: string | null = null;
  const rawPrice = form.pricePerSong;
  if (isEmptyInput(rawPrice)) {
    price = 'Enter a price per song.';
  } else if (typeof rawPrice !== 'number' || !Number.isFinite(rawPrice)) {
    price = 'Enter a price per song.';
  } else if (rawPrice <= 0) {
    price = 'Price per song must be greater than 0.';
  } else if (toCents(rawPrice) === null) {
    price = `Use at most ${KONOBOX_PRICING_LIMITS.maxDecimals} decimal places (for example 2.50).`;
  }

  const counts: (string | null)[] = Array.from({ length: KONOBOX_PACKAGE_SLOTS }, (_, i) => {
    const raw = form.songCounts?.[i];
    if (isEmptyInput(raw) || typeof raw !== 'number' || !Number.isFinite(raw)) return 'Enter the number of songs.';
    if (!Number.isInteger(raw)) return 'Use a whole number (no decimals).';
    if (raw < KONOBOX_PRICING_LIMITS.minSongs) return 'Number of songs must be at least 1.';
    if (raw > KONOBOX_PRICING_LIMITS.maxSongs) return 'Number of songs cannot be more than 20.';
    return null;
  });

  // Duplicates mark every field that holds the repeated (otherwise valid) value.
  const seen = new Map<number, number[]>();
  counts.forEach((err, i) => {
    if (err) return;
    const value = form.songCounts[i] as number;
    seen.set(value, [...(seen.get(value) ?? []), i]);
  });
  seen.forEach(indexes => {
    if (indexes.length > 1) {
      indexes.forEach(i => { counts[i] = 'Each package needs a different number of songs.'; });
    }
  });

  return { price, counts, hasErrors: price !== null || counts.some(c => c !== null) };
}

export interface KonoboxPreviewChip {
  label: string;
  valid: boolean;
}

export function formatKonoboxPackageLabel(songCount: number, pricePerSong: number): string {
  const word = songCount === 1 ? 'SONG' : 'SONGS';
  return `${songCount} ${word} - $${packagePrice(songCount, pricePerSong).toFixed(2)}`;
}

/** Four chips for the "Kiosk preview": valid packages ascending, then `--` for the unusable slots. */
export function konoboxPreviewChips(form: {
  pricePerSong: unknown;
  songCounts: unknown[];
}): KonoboxPreviewChip[] {
  const errors = validateKonoboxPricingForm(form);
  const chips: KonoboxPreviewChip[] = [];
  if (errors.price === null) {
    const valid = errors.counts
      .map((err, i) => (err === null ? (form.songCounts[i] as number) : null))
      .filter((c): c is number => c !== null)
      .sort((a, b) => a - b);
    valid.forEach(c => chips.push({ label: formatKonoboxPackageLabel(c, form.pricePerSong as number), valid: true }));
  }
  while (chips.length < KONOBOX_PACKAGE_SLOTS) chips.push({ label: '--', valid: false });
  return chips;
}

/** Saved shape: price per song in dollars (from cents) and the four counts sorted ascending. */
export function konoboxPricingToStored(form: {
  pricePerSong: number;
  songCounts: number[];
}): { pricePerSong: number; songCounts: number[] } {
  const cents = toCents(form.pricePerSong) as number;
  return {
    pricePerSong: cents / 100,
    songCounts: [...form.songCounts].sort((a, b) => a - b)
  };
}

/** Legacy store fields removed when a store is saved (AC-106). */
export const KONOBOX_LEGACY_PRICE_KEYS = [
  'songPrices',
  'priceOneSong', 'priceTwoSong', 'priceThreeSong', 'priceFourSong',
  'priceSong1', 'priceSong2', 'priceSong3', 'priceSong4'
] as const;
